import { Clock, MapPin, DollarSign, Users, Star, Shield, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { JobCardProps } from './types';

const JobCard = ({ job, onJobClick, showApplicationButton = true, compact = false }: JobCardProps) => {
  const formatBudget = (min?: number, max?: number, currency?: string) => {
    if (!min && !max) return 'Budget not specified';
    
    const currencySymbol = currency === 'BRO_SCORE' ? 'BS' : currency === 'TOP1_TOKEN' ? '$TOP1' : '$';
    
    if (min && max) {
      return `${currencySymbol}${min}-${max}`;
    }
    if (min) {
      return `${currencySymbol}${min}+`;
    }
    if (max) {
      return `Up to ${currencySymbol}${max}`;
    }
    return 'Budget negotiable';
  };

  const getExperienceBadgeColor = (level: string) => {
    switch (level) {
      case 'entry': return 'bg-green-500';
      case 'intermediate': return 'bg-yellow-500';
      case 'expert': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <Zap className="text-red-400" size={14} />;
      case 'high': return <Star className="text-yellow-400" size={14} />;
      default: return null;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    }
    if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    }
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <div 
      className={`bg-navy-light rounded-lg border border-tan-dark hover:border-tan transition-all cursor-pointer ${
        compact ? 'p-3' : 'p-4'
      }`}
      onClick={() => onJobClick(job)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {job.featured && (
              <Star className="text-yellow-400 fill-current" size={16} />
            )}
            <h3 className={`font-bold text-tan ${compact ? 'text-sm' : 'text-base'}`}>
              {job.title}
            </h3>
            {getPriorityIcon(job.priority)}
          </div>
          
          <div className="flex items-center gap-3 text-xs text-tan-dark">
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {formatTimeAgo(job.created_at)}
            </span>
            {job.category && (
              <span className="flex items-center gap-1">
                <span>{job.category.icon}</span>
                {job.category.name}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-1">
          <Badge className={`${getExperienceBadgeColor(job.experience_level)} text-white text-xs px-2 py-1`}>
            {job.experience_level}
          </Badge>
          {job.requires_verification && (
            <Shield className="text-tan" size={14} />
          )}
        </div>
      </div>

      {/* Description */}
      {!compact && (
        <p className="text-tan-dark text-sm mb-3 line-clamp-2">
          {job.description.length > 120 
            ? `${job.description.substring(0, 120)}...` 
            : job.description
          }
        </p>
      )}

      {/* Skills */}
      <div className="flex flex-wrap gap-1 mb-3">
        {job.required_skills.slice(0, compact ? 2 : 4).map((skill, index) => (
          <span
            key={index}
            className="bg-navy rounded px-2 py-1 text-xs text-tan-dark border border-tan-dark"
          >
            {skill}
          </span>
        ))}
        {job.required_skills.length > (compact ? 2 : 4) && (
          <span className="text-xs text-tan-dark">
            +{job.required_skills.length - (compact ? 2 : 4)} more
          </span>
        )}
      </div>

      {/* Budget and Details */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1 text-tan font-bold">
          <DollarSign size={14} />
          <span className={compact ? 'text-sm' : 'text-base'}>
            {formatBudget(job.budget_min, job.budget_max, job.budget_currency)}
          </span>
        </div>
        
        <div className="flex items-center gap-3 text-xs text-tan-dark">
          {job.applications_count !== undefined && (
            <span className="flex items-center gap-1">
              <Users size={12} />
              {job.applications_count} applied
            </span>
          )}
          
          {job.estimated_duration && (
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {job.estimated_duration}
            </span>
          )}
        </div>
      </div>

      {/* Job Properties */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {job.remote_allowed && (
            <Badge variant="outline" className="text-xs border-tan-dark text-tan-dark">
              Remote
            </Badge>
          )}
          
          {job.payment_type === 'milestone' && (
            <Badge variant="outline" className="text-xs border-tan-dark text-tan-dark">
              Milestone
            </Badge>
          )}
          
          {job.min_bro_score > 0 && (
            <Badge variant="outline" className="text-xs border-tan-dark text-tan-dark">
              {job.min_bro_score}+ BS
            </Badge>
          )}
        </div>

        {showApplicationButton && (
          <Button
            size="sm"
            className="bg-tan text-navy hover:bg-tan-dark"
            onClick={(e) => {
              e.stopPropagation();
              onJobClick(job);
            }}
          >
            Apply
          </Button>
        )}
      </div>

      {/* Location */}
      {job.location && (
        <div className="flex items-center gap-1 mt-2 text-xs text-tan-dark">
          <MapPin size={12} />
          {job.location}
        </div>
      )}
    </div>
  );
};

export default JobCard;